import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  productFeaturesList: [],
  totalItems: 0,
  ItemsOnPage: 0,
  productFeature: null,
};

// Async thunks
export const getProductFeaturesPagination = createAsyncThunk(
  'productFeatures/getProductFeaturesPagination',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/admin/product-features', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getProductFeatures = createAsyncThunk(
  'productFeatures/getProductFeatures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/admin/product-features');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getProductFeatureById = createAsyncThunk(
  'productFeatures/getProductFeatureById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/admin/product-features/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProductFeature = createAsyncThunk(
  'productFeatures/createProductFeature',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/admin/product-features', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProductFeature = createAsyncThunk(
  'productFeatures/updateProductFeature',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/admin/product-features/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProductFeature = createAsyncThunk(
  'productFeatures/deleteProductFeature',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/admin/product-features/${id}`);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const slice = createSlice({
  name: 'productFeatures',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get Product Features Pagination
      .addCase(getProductFeaturesPagination.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProductFeaturesPagination.fulfilled, (state, action) => {
        state.isLoading = false;
        // The API returns { success: true, data: [...] }
        const responseData = action.payload.data || action.payload.items || action.payload;
        const items = Array.isArray(responseData) ? responseData : [];
        
        state.productFeaturesList = items;
        state.totalItems = action.payload.totalItems || action.payload.total || items.length;
        state.ItemsOnPage = action.payload.ItemsOnPage || action.payload.currentPageItems || items.length;
      })
      .addCase(getProductFeaturesPagination.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Product Features
      .addCase(getProductFeatures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProductFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productFeaturesList = action.payload.data || action.payload.items || [];
      })
      .addCase(getProductFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Product Feature By ID
      .addCase(getProductFeatureById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProductFeatureById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productFeature = action.payload.data || action.payload;
      })
      .addCase(getProductFeatureById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Product Feature
      .addCase(createProductFeature.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProductFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productFeaturesList.unshift(action.payload.data || action.payload);
      })
      .addCase(createProductFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Product Feature
      .addCase(updateProductFeature.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProductFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedFeature = action.payload.data || action.payload;
        const index = state.productFeaturesList.findIndex(item => item._id === updatedFeature._id);
        if (index !== -1) {
          state.productFeaturesList[index] = updatedFeature;
        }
      })
      .addCase(updateProductFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Product Feature
      .addCase(deleteProductFeature.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProductFeature.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productFeaturesList = state.productFeaturesList.filter(item => item._id !== action.payload.id);
      })
      .addCase(deleteProductFeature.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export default slice.reducer;
